import type { InputFrame } from '../core/input';
import type { StatMod } from '../content/types';
import type { SkillFlag } from '../content/skills';
import type { StoredRunConfig } from '../meta/runPersistence';
import type { SimCommand } from '../sim/sim';

/**
 * ONLINE CO-OP WIRE PROTOCOL (docs/MULTIPLAYER.md § Step 2)
 *
 * Host-relayed deterministic lockstep: every peer runs the same `Sim`; guests
 * send their per-frame input to the host, the host bundles every player's input
 * (plus any commands) for frame F into one authoritative `frame` message and
 * broadcasts it, and every peer — host included — executes exactly those
 * bundles in order. Messages are JSON over one ordered, reliable channel.
 *
 * Bump NET_PROTOCOL_VERSION whenever a message shape changes; peers with a
 * different protocol or game version are rejected at `hello`.
 */
export const NET_PROTOCOL_VERSION = 3;

/** Movement axes as integers in [-127, 127] plus a button bitmask (1 = dash, 2 = action). */
export type WireInput = [number, number, number];

export const EMPTY_WIRE_INPUT: WireInput = [0, 0, 0];
const AXIS = 127;
export const BUTTON_DASH = 1;
export const BUTTON_ACTION = 2;

/**
 * Quantize a local input frame for the wire. Every peer — including the host
 * for its own input — feeds the sim the *dequantized* value, so all peers see
 * bit-identical inputs.
 */
export function quantizeInput(f: InputFrame): WireInput {
  const q = (v: number) => Math.max(-AXIS, Math.min(AXIS, Math.round(v * AXIS)));
  return [q(f.mx), q(f.my), (f.dash ? BUTTON_DASH : 0) | (f.action ? BUTTON_ACTION : 0)];
}

export function dequantizeInput(w: WireInput, out: InputFrame): InputFrame {
  out.mx = w[0] / AXIS;
  out.my = w[1] / AXIS;
  out.dash = (w[2] & BUTTON_DASH) !== 0;
  out.action = (w[2] & BUTTON_ACTION) !== 0;
  return out;
}

export function isWireInput(value: unknown): value is WireInput {
  return Array.isArray(value) && value.length === 3 && value.every((n) => Number.isInteger(n) && Math.abs(n) <= 255);
}

/** What a player brings to an online run from their own profile. */
export interface CoopLoadout {
  name: string;
  character: string;
  /** Host rebuilds skill power from these fields; guest-supplied metaMods are never trusted. */
  characterLevel?: number;
  skillAllocations?: Record<string, number>;
  skillFlags?: SkillFlag[];
  /** Equipped item ids (a weapon is guaranteed by the host if missing). */
  equipment: string[];
  pet: string | null;
  petLevel?: number;
  /** Assigned Observatory pets; host rebuilds their deterministic profile bonuses by id. */
  housePets?: string[];
  startingHeals?: number;
  metaMods: StatMod[];
  /** Upgrade-pool unlock tags. */
  unlocked: string[];
  rerolls: number;
  banishes: number;
}

export interface RosterEntry {
  /** Stable per-connection id ('host' or the guest's signalling id). */
  id: string;
  name: string;
  character: string;
  weapon: string;
  pet: string | null;
  ready: boolean;
  host: boolean;
  /** Round-trip time to the host in ms, when known. */
  ping: number | null;
}

export interface LobbySettings {
  startEra: number;
  paradox: number;
}

export type GuestMessage =
  | { t: 'hello'; protocol: number; gameVersion: string; loadout: CoopLoadout; key?: string }
  | { t: 'loadout'; loadout: CoopLoadout }
  | { t: 'ready'; ready: boolean }
  | { t: 'in'; f: number; i: WireInput; c?: SimCommand[] }
  | { t: 'hash'; f: number; h: number }
  | { t: 'leave' };

export type HostMessage =
  | { t: 'welcome' }
  | { t: 'reject'; reason: string }
  | { t: 'lobby'; roster: RosterEntry[]; settings: LobbySettings; you: string }
  | { t: 'start'; config: StoredRunConfig; index: number; names: string[]; delay: number }
  | { t: 'frame'; f: number; i: WireInput[]; c?: [number, SimCommand][] }
  | { t: 'resync'; f: number; hold: number; snap: string }
  | { t: 'left'; player: number; name: string }
  | { t: 'rejoined'; player: number; name: string }
  | { t: 'delay'; d: number }
  | { t: 'end'; reason: string };

/** Either side may measure round-trip time; the receiver echoes `n`. */
export type PingMessage = { t: 'ping'; n: number } | { t: 'pong'; n: number };

export type NetMessage = GuestMessage | HostMessage | PingMessage;

const COMMAND_TYPES = new Set(['chooseUpgrade', 'reroll', 'banish', 'advanceStage']);

/** Structural check for a command received from the network (never trust a peer's JSON). */
export function isSimCommand(value: unknown): value is SimCommand {
  if (!value || typeof value !== 'object') return false;
  const c = value as Record<string, unknown>;
  if (typeof c.type !== 'string' || !COMMAND_TYPES.has(c.type)) return false;
  if (c.type === 'advanceStage') return c.destinationEra === undefined || Number.isInteger(c.destinationEra);
  if (!Number.isInteger(c.player)) return false;
  if (c.type === 'chooseUpgrade' || c.type === 'banish') return typeof c.id === 'string' && c.id.length <= 64;
  return true;
}
